import React from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const ChartInfoTooltip = ({ text }) => {
  const [visible, setVisible] = React.useState(false);
  const tooltipId = React.useId();

  if (!text) return null;

  return (
    <span className="chart-info-tooltip">
      <button
        type="button"
        className="chart-info-tooltip__trigger"
        aria-label="How to read this chart"
        aria-describedby={visible ? tooltipId : undefined}
        aria-expanded={visible}
        onFocus={() => setVisible(true)}
        onBlur={() => setVisible(false)}
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        onClick={() => setVisible(current => !current)}
      >
        ⓘ
      </button>
      <span id={tooltipId} role="tooltip" className="chart-info-tooltip__content" hidden={!visible}>
        {text}
      </span>
    </span>
  );
};

const HistogramChart = ({ title, subtitle, bins, datasets, xAxisLabel, yAxisLabel, color = '#3498db', infoText, showLegend }) => {
  // Handle empty or invalid bins data
  const validBins = Array.isArray(bins) && bins.length > 0 ? bins : [];
  const normalizedDatasets = Array.isArray(datasets) && datasets.length > 0
    ? datasets.map(dataset => ({
      label: dataset.label || yAxisLabel || 'Frequency',
      color: dataset.color || color,
      bins: Array.isArray(dataset.bins) ? dataset.bins : []
    }))
    : [{ label: yAxisLabel || 'Frequency', color, bins: validBins }];
  const chartLabels = [...new Set(normalizedDatasets.flatMap(dataset =>
    dataset.bins.map(bin => String(bin.range))))];

  // If no data, show empty state
  if (chartLabels.length === 0) {
    return (
      <div className="chart-container-wrapper">
        <h4 className="chart-title">{title}<ChartInfoTooltip text={infoText} /></h4>
        {subtitle && <p className="chart-subtitle">{subtitle}</p>}
        <div className="chart-canvas-container">
          <div className="blank-chart-placeholder">
            <p>No data available</p>
          </div>
        </div>
      </div>
    );
  }

  const chartData = {
    labels: chartLabels,
    datasets: normalizedDatasets.map(dataset => ({
      label: dataset.label,
      data: chartLabels.map(label => dataset.bins.find(bin => String(bin.range) === label)?.count || 0),
      backgroundColor: dataset.color + '80', // Add transparency
      borderColor: dataset.color,
      borderWidth: 1,
      borderRadius: 4,
      borderSkipped: false,
    }))
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: showLegend ?? normalizedDatasets.length > 1
      },
      tooltip: {
        callbacks: {
          title: function(context) {
            return `${xAxisLabel || 'Range'}: ${context[0].label}`;
          },
          label: function(context) {
            const total = normalizedDatasets[context.datasetIndex].bins.reduce((sum, bin) => sum + bin.count, 0);
            const percentage = total > 0 ? ((context.parsed.y / total) * 100).toFixed(1) : '0.0';
            return normalizedDatasets.length > 1
              ? `${context.dataset.label} unique devices: ${context.parsed.y}`
              : `${yAxisLabel || 'Count'}: ${context.parsed.y} (${percentage}%)`;
          }
        }
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: xAxisLabel || 'Range'
        },
        grid: {
          display: false
        }
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: yAxisLabel || 'Frequency'
        },
        grid: {
          color: '#e0e0e0'
        },
        ticks: {
          precision: 0 // Show whole numbers only
        }
      }
    }
  };

  return (
    <div className="chart-container-wrapper">
      <h4 className="chart-title">{title}<ChartInfoTooltip text={infoText} /></h4>
      {subtitle && <p className="chart-subtitle">{subtitle}</p>}
      <div className="chart-canvas-container">
        <Bar data={chartData} options={options} />
      </div>
    </div>
  );
};

export default HistogramChart;
